import React, { useMemo } from "react";
import { Button, DownloadIcon, Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { useLicensesGoBack } from "~/entity/Licenses";
import {
  LICENSES_EMPLOYEE_SORT_TO_API,
  mapLicensesSortToQuery,
  mapLicenseTypeToApi,
} from "~/entity/Licenses/model/licenseQueryParams";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useURLTableSort } from "~/shared/ui/TableV2";

import {
  getPositionGroupColumns,
  POSITION_GROUP_SORTABLE_COLUMNS,
} from "./model/columns";
import { ILicensePositionGroupUrlParams } from "./model/types";
import { usePositionGroupLicenses } from "./model/usePositionGroupLicenses";

import s from "./LicensePositionGroup.less";

const LicensePositionGroup = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const { handleCount, handlePage } = usePaginationParams("urlParams");
  const goBack = useLicensesGoBack();
  const urlParams = useURLParamsGetter<ILicensePositionGroupUrlParams>();
  const {
    instanceId = "",
    positionName = "",
    dealerId = "",
    organizationId = "",
    organizationName = "",
    licenseType = "",
    page,
    count = 25,
    sortField,
    sortType: sortTypeRaw,
  } = urlParams;

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns: POSITION_GROUP_SORTABLE_COLUMNS,
    setParams,
  });

  const tableSortField =
    sortField && POSITION_GROUP_SORTABLE_COLUMNS.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  const queryParams = useMemo(
    () => ({
      dealerId: dealerId ? Number(dealerId) : undefined,
      organizationId: organizationId ? String(organizationId) : undefined,
      instanceId: instanceId ? Number(instanceId) : undefined,
      licenseType: mapLicenseTypeToApi(String(licenseType)),
      page: pageNumber,
      count: pageSize,
      ...mapLicensesSortToQuery(
        tableSortField,
        normalizedSortType,
        LICENSES_EMPLOYEE_SORT_TO_API,
      ),
    }),
    [
      dealerId,
      organizationId,
      instanceId,
      licenseType,
      pageNumber,
      pageSize,
      tableSortField,
      normalizedSortType,
    ],
  );

  const { rows, total, isLoading } = usePositionGroupLicenses(
    queryParams,
    String(organizationName),
  );

  const columns = useMemo(
    () =>
      getPositionGroupColumns({
        t,
        sortField: tableSortField,
        sortType: normalizedSortType,
        sortableColumns: POSITION_GROUP_SORTABLE_COLUMNS,
      }),
    [t, tableSortField, normalizedSortType],
  );

  const { notifyWarning } = useNotify();

  const handleExportExcel = () => {
    notifyWarning(t("licensePositionGroup.notification.excelUnavailable"));
  };

  return (
    <Wrapper.Content
      title={t("licensePositionGroup.title", { positionName })}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
    >
      <div className={s.downloadToolbar}>
        <Button
          bold
          color="secondary"
          variant="filled"
          fontSize="10"
          size="small"
          radius={30}
          icon={<DownloadIcon size={12} />}
          onClick={handleExportExcel}
        >
          {t("licensePositionGroup.downloadExcel")}
        </Button>
      </div>

      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: "max-content" }}
        sortableColumns={POSITION_GROUP_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
      />

      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
        selectFrom={[25, 50, 100]}
      />
    </Wrapper.Content>
  );
};

export default LicensePositionGroup;
